import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslate } from '@tolgee/react';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { isFileFontReady, loadFontFromFile } from '../../../utils/fonts';

export interface WebFont {
  family: string;
  category: string;
  variants: string[];
  subsets: string[];
  version: string;
  lastModified: string;
  files: Record<string, string>;
  // Google ships a per-family "menu" file: the face subset down to the glyphs
  // of its own name. A few KB instead of a few hundred, which is what a list of
  // 1799 families needs to preview itself.
  menu?: string;
  selectedVariant?: any | undefined;
}

// What to show while a face is still downloading, and if it never arrives. A
// family from the same genre keeps the preview honest rather than dropping
// every font back to the same UI sans.
const GENERIC_BY_CATEGORY: Record<string, string> = {
  serif: 'serif',
  'sans-serif': 'sans-serif',
  display: 'sans-serif',
  handwriting: 'cursive',
  monospace: 'monospace'
};

const genericFor = (font: WebFont): string =>
  GENERIC_BY_CATEGORY[font.category] || 'sans-serif';

// The menu face is registered under its own name so it can never be picked for
// the preview paragraph, which needs the full glyph set and would otherwise
// render half its characters from a fallback.
const menuFamily = (font: WebFont): string => `Lomavis Menu ${font.family}`;

// Not every family ships a `regular` cut — Buda, Molle, Sunflower and
// UnifrakturCook only have weighted or italic ones. Reading `files.regular`
// alone left those four with no preview at all.
const previewFileUrl = (font: WebFont): string | undefined => {
  const files = font.files || {};
  return files.regular || files['400'] || Object.values(files)[0];
};

/**
 * One row of the dropdown, set in the family it offers. Without this every
 * entry rendered in the UI font and the list read as 1799 copies of the same
 * face.
 *
 * The menu holds 100 results at a time but shows about ten, so the face is
 * fetched once the row is actually scrolled into view — otherwise opening the
 * picker cost 100 requests and 300 KB before the user had read a single name.
 */
const FontMenuItem: React.FC<{ font: WebFont }> = ({ font }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const family = menuFamily(font);
  const [ready, setReady] = useState(() => isFileFontReady(family));

  useEffect(() => {
    let active = true;

    // Wait for the face and put it on the row yourself. Browsers do reflow
    // text when a matching face lands in document.fonts, but leaving the row
    // to that is unverifiable from here: nothing in this component knows
    // whether the family ever became usable.
    const load = async (): Promise<void> => {
      const available = await loadFontFromFile(
        family,
        font.menu || previewFileUrl(font)
      );
      if (active) setReady(available);
    };

    const row = rowRef.current;
    if (!row || typeof IntersectionObserver === 'undefined') {
      void load();
      return () => {
        active = false;
      };
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        void load();
      }
    });
    observer.observe(row);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [font, family]);

  return (
    <div
      ref={rowRef}
      // The row carries its own verdict so a broken face is visible as a
      // broken face, in the DOM and in a test, instead of looking like a
      // family that merely resembles the UI font.
      data-font-ready={ready}
      style={{
        fontFamily: ready
          ? `'${family}', ${genericFor(font)}`
          : genericFor(font),
        fontSize: 18,
        lineHeight: 1.6
      }}
    >
      {font.family}
    </div>
  );
};

interface FontSelectorProps {
  selectedFont: WebFont | null; // From parent
  onFontChange: (font: WebFont | null) => void;
  options: WebFont[];
  filter?: (font: WebFont) => boolean;
}

export const FontSelector: React.FC<FontSelectorProps> = ({
  selectedFont,
  onFontChange,
  options,
  filter
}) => {
  const { t } = useTranslate();

  const filteredOptions = useMemo(
    () => (filter ? options.filter(filter) : options),
    [options, filter]
  );

  // Derived, not mirrored in state: the parent owns the choice. Holding a copy
  // here meant `filter` callers — whose `filteredOptions` is a fresh array on
  // every render — re-ran the sync effect and set a fresh array back, forever.
  const selected = useMemo(() => {
    if (!selectedFont) return [];
    const match = filteredOptions.find(
      (item) => item.family === selectedFont.family
    );
    return match ? [match] : [];
  }, [selectedFont, filteredOptions]);

  const previewFamily = selectedFont ? selectedFont.family : null;

  useEffect(() => {
    if (!selectedFont) return;
    void loadFontFromFile(selectedFont.family, previewFileUrl(selectedFont));
  }, [selectedFont]);

  const handleChange = (selectedItems: any): void => {
    const chosen = selectedItems[0];
    onFontChange(chosen || null);
  };

  return (
    <div>
      <Typeahead
        id="google-font-selector"
        labelKey="family" // We match by 'family' field
        options={filteredOptions}
        placeholder={t(
          'lomavis.settings.branding.template_branding.font_selector_placeholder'
        )}
        onChange={handleChange}
        selected={selected}
        renderMenuItemChildren={(option) => (
          <FontMenuItem font={option as WebFont} />
        )}
      />

      <div className="mt-3" />

      <div className="w-100 card shadow-sm p-5 text-center">
        <p
          style={{
            fontFamily: previewFamily
              ? `'${previewFamily}', ${genericFor(selectedFont)}`
              : undefined,
            fontSize: 15
          }}
        >
          {t('lomavis.settings.branding.template_branding.font_preview')}
        </p>
      </div>
    </div>
  );
};
